/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import { List, Loader } from 'semantic-ui-react';
import { Table } from '../../../components';
import { FormattedMessage } from 'react-intl';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { MASTER_CABINET_LIST_FRAGMENT } from '../../appointments/containers/masterAccessQueries';

import EditCabinetButton from './EditCabinetButton';
import DeleteCabinetButton from './DeleteCabinetButton';

const NameCell = styled(Table.Cell)`
    padding: 0px !important;
`;

export const GET_CABINETS_QUERY = gql`
    query getCabinets($salon: ID!, $lang: String) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    cabinets {
                        id
                        name(lang: $lang)
                        nameVariants {
                            en
                            ru
                            lv
                        }
                        serviceGroups {
                            id
                            name(lang: $lang)
                            type
                        }
                    }
                }
            }
            ...onMasterCabinetList
        }
    }
    ${MASTER_CABINET_LIST_FRAGMENT}
`;

/**
 * Show this message if list of masters is empty
 */
const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={2} textAlign="center">
            <FormattedMessage
                id="pages.cabinets.CabinetList.noCabinets"
                defaultMessage="You don't have cabinets yet"
            />
        </Table.Cell>
    </Table.Row>
);

/**
 * Represents component with single row of cabinet details
 */
const CabinetRow = ({ cabinet }) => (
    <Table.Row>
        <Table.Cell>
            <span>{cabinet.name}</span>
        </Table.Cell>
        <Table.Cell>
            <List horizontal divided>
                <List.Item>
                    <EditCabinetButton cabinet={cabinet} />
                </List.Item>
                <List.Item>
                    <DeleteCabinetButton cabinet={cabinet} />
                </List.Item>
            </List>
        </Table.Cell>
    </Table.Row>
);

function CabinetList() {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(({ intl }) => intl.get('locale'));
    const searchQuery = useSelector(({ staff }) => staff.get('searchQuery'));

    const { data, loading } = useQuery(GET_CABINETS_QUERY, {
        variables: {
            salon,
            search: searchQuery || undefined,
            lang: currentLanguage,
        },
        fetchPolicy: 'cache-and-network',
    });

    if (loading) {
        return <Loader size="big" inline="centered" active />;
    }

    const cabinets = data?.viewer?.salon?.cabinets ?? [];

    return (
        <Table celled selectable striped fixed>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>
                        <FormattedMessage
                            id="pages.cabinets.CabinetList.name"
                            defaultMessage="Name"
                        />
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        <FormattedMessage
                            id="pages.cabinets.CabinetList.actions"
                            defaultMessage="Actions"
                        />
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {isEmpty(cabinets) ? (
                    <EmptyMessage />
                ) : (
                    cabinets.map(
                        cabinet =>
                            !!cabinet && (
                                <CabinetRow key={cabinet.id} cabinet={cabinet} />
                            ),
                    )
                )}
            </Table.Body>
        </Table>
    );
}

export default CabinetList;
